import React from 'react';
import { ShoppingCart, Trash2, Loader2, Navigation } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';

const CartSheet = ({
  cartOpen,
  setCartOpen,
  cart,
  removeFromCart,
  getTotalCart,
  deliveryMethod,
  setDeliveryMethod,
  deliveryInfo,
  selectedLocation,
  setSelectedLocation,
  customerName,
  setCustomerName,
  customerPhone,
  setCustomerPhone,
  scheduledDate,
  setScheduledDate,
  scheduledTime,
  setScheduledTime,
  comments,
  setComments,
  loadingDelivery,
  deliveryError,
  handleGetLocation,
  handleWhatsAppOrder,
  generateOrderNumber,
  validateScheduledTime,
  getBusinessHoursForDate,
  isSubmitting
}) => {
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetTrigger asChild>
        <Button className="bg-[#4a2c2a] hover:bg-[#3a1c1a] text-white relative text-sm md:text-base px-3 md:px-4 shadow-lg hover:shadow-xl transition-all duration-200">
          <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 md:mr-2" />
          <span className="hidden md:inline">Ver Carrito</span>
          {cart.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {cart.length}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-2xl text-chocolate">Tu Pedido</SheetTitle>
          <SheetDescription>
            Completa los datos para finalizar tu orden
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 space-y-6">
          {/* Cart Items */}
          {cart.length > 0 ? (
            <div className="space-y-4">
              <h3 className="font-semibold text-chocolate">Productos ({cart.length})</h3>
              {cart.map((item, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-start justify-between bg-cream p-4 rounded-lg">
                    <div className="flex-1">
                      <p className="font-semibold text-chocolate">{item.name}</p>
                      <p className="text-sm text-chocolate-light">{item.type} - {item.size}</p>
                      <p className="text-lg font-bold text-caramel mt-1">${item.price.toFixed(2)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFromCart(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {item.isMenuReina && (
                    <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                      <p className="text-xs font-semibold text-red-900">⏰ Aviso Importante:</p>
                      <p className="text-xs text-red-800 mt-1">
                        Este pedido requiere al menos 24 horas para su preparación y debe recibir confirmación de inicio de preparación
                      </p>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-chocolate">Subtotal:</span>
                  <span className="text-chocolate">${getTotalCart().toFixed(2)}</span>
                </div>
                {deliveryMethod === 'delivery' && deliveryInfo && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-chocolate-light">Envío ({deliveryInfo.distance_km} km):</span>
                    {deliveryInfo.beyond_max_distance ? (
                      <span className="text-amber-600 font-semibold">Bajo consulta</span>
                    ) : (
                      <span className="text-chocolate-light">${deliveryInfo.delivery_cost.toFixed(2)}</span>
                    )}
                  </div>
                )}
                <div className="flex justify-between items-center text-xl font-bold pt-2 border-t">
                  <span className="text-chocolate">Total:</span>
                  {deliveryMethod === 'delivery' && deliveryInfo && deliveryInfo.beyond_max_distance ? (
                    <div className="text-right">
                      <span className="text-caramel">${getTotalCart().toFixed(2)}</span>
                      <span className="text-xs text-amber-600 block">+ envío</span>
                    </div>
                  ) : (
                    <span className="text-caramel">
                      ${(getTotalCart() + (deliveryMethod === 'delivery' && deliveryInfo ? deliveryInfo.delivery_cost : 0)).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-chocolate-light">
              <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>Tu carrito está vacío</p>
            </div>
          )}

          {/* Order Form */}
          {cart.length > 0 && (
            <div className="space-y-4 border-t pt-6">
              <h3 className="font-semibold text-chocolate">Datos del Pedido</h3>
              
              {/* Order Number Preview */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="text-2xl">📋</div>
                    <div>
                      <p className="text-xs text-blue-600 font-semibold">N° de Pedido</p>
                      <p className="text-sm font-mono font-bold text-blue-900">{generateOrderNumber()}</p>
                    </div>
                  </div>
                  <div className="text-xs text-blue-600">
                    {new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
              
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="customerName">Nombre *</Label>
                <Input
                  id="customerName"
                  placeholder="Tu nombre completo"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="border-chocolate/20"
                />
              </div>

              {/* Delivery Method */}
              <div className="space-y-2">
                <Label>Método de Entrega *</Label>
                <RadioGroup value={deliveryMethod} onValueChange={setDeliveryMethod} className="space-y-2">
                  <label className="flex items-start space-x-3 border-2 rounded-lg p-3 cursor-pointer transition-all hover:border-caramel">
                    <RadioGroupItem value="tienda" id="tienda" className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold leading-5">Retiro en Tienda</div>
                      <div className="text-xs text-chocolate-light">Sin costo adicional</div>
                    </div>
                  </label>
                  <label className="flex items-start space-x-3 border-2 rounded-lg p-3 cursor-pointer transition-all hover:border-caramel">
                    <RadioGroupItem value="delivery" id="delivery" className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold leading-5">Delivery a Domicilio</div>
                      <div className="text-xs text-chocolate-light">Costo según distancia</div>
                    </div>
                  </label>
                </RadioGroup>
              </div>

              {/* Location Selection (if tienda) */}
              {deliveryMethod === 'tienda' && (
                <div className="space-y-2">
                  <Label>Selecciona la Tienda *</Label>
                  <RadioGroup value={selectedLocation} onValueChange={setSelectedLocation} className="space-y-2">
                    <label className="flex items-start space-x-3 border-2 rounded-lg p-3 cursor-pointer transition-all hover:border-caramel">
                      <RadioGroupItem value="matriz" id="matriz" className="flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold leading-5">Matriz</div>
                        <div className="text-xs text-chocolate-light">Sergio Jativa 175-147</div>
                      </div>
                    </label>
                    <label className="flex items-start space-x-3 border-2 rounded-lg p-3 cursor-pointer transition-all hover:border-caramel">
                      <RadioGroupItem value="sucursal" id="sucursal" className="flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold leading-5">Sucursal</div>
                        <div className="text-xs text-chocolate-light">Av. Eloy Alfaro N33-21</div>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              )}

              {/* Delivery Section */}
              {deliveryMethod === 'delivery' && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-900 mb-3">📍 Calcula el costo de envío</p>
                    <p className="text-xs text-blue-700 mb-3">
                      Tarifa: $2.00 hasta 4 km + $0.50 por km adicional (máx 10 km)
                    </p>
                    
                    <Button
                      onClick={handleGetLocation}
                      disabled={loadingDelivery}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {loadingDelivery ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Calculando...
                        </>
                      ) : (
                        <>
                          <Navigation className="w-4 h-4 mr-2" />
                          Compartir mi ubicación GPS
                        </>
                      )}
                    </Button>
                    
                    {deliveryError && (
                      <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-700">{deliveryError}</p>
                      </div>
                    )}
                    
                    {deliveryInfo && (
                      <div className={`mt-3 rounded-lg p-3 space-y-2 ${deliveryInfo.beyond_max_distance ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                        <div className="flex items-center space-x-2">
                          <span className={deliveryInfo.beyond_max_distance ? 'text-amber-600' : 'text-green-600'}>
                            {deliveryInfo.beyond_max_distance ? '⚠️' : '✅'}
                          </span>
                          <span className={`font-semibold ${deliveryInfo.beyond_max_distance ? 'text-amber-800' : 'text-green-800'}`}>
                            Ubicación detectada
                          </span>
                        </div>
                        
                        {deliveryInfo.after_hours_warning && (
                          <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-3">
                            <div className="flex items-start space-x-2">
                              <span className="text-orange-600 text-lg flex-shrink-0">⏰</span>
                              <div>
                                <p className="text-sm font-bold text-orange-900">Aviso de Horario</p>
                                <p className="text-xs text-orange-700 mt-1">{deliveryInfo.after_hours_warning}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {deliveryInfo.address && (
                          <p className={`text-xs truncate ${deliveryInfo.beyond_max_distance ? 'text-amber-700' : 'text-green-700'}`}>
                            📍 {deliveryInfo.address}
                          </p>
                        )}
                        
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="bg-white rounded p-2 text-center">
                            <div className="text-xs text-gray-500">Distancia (Google Maps)</div>
                            <div className={`font-bold ${deliveryInfo.beyond_max_distance ? 'text-amber-700' : 'text-green-700'}`}>
                              {deliveryInfo.google_distance_text || `${deliveryInfo.distance_km} km`}
                            </div>
                          </div>
                          <div className="bg-white rounded p-2 text-center">
                            <div className="text-xs text-gray-500">Tiempo est.</div>
                            <div className={`font-bold ${deliveryInfo.beyond_max_distance ? 'text-amber-700' : 'text-green-700'}`}>
                              {deliveryInfo.estimated_time}
                            </div>
                          </div>
                        </div>
                        
                        {deliveryInfo.origin_address && (
                          <div className="text-xs text-gray-500 text-center mt-1">
                            Desde: {deliveryInfo.origin_address}
                          </div>
                        )}
                        
                        {deliveryInfo.beyond_max_distance ? (
                          <div className="bg-amber-100 rounded-lg p-3 text-center">
                            <div className="text-xs text-amber-800 mb-1">Costo de envío</div>
                            <div className="text-lg font-bold text-amber-700">Consultar tarifa con el local</div>
                            <p className="text-xs text-amber-600 mt-1">
                              Envíos mayores a 10 km requieren cotización especial
                            </p>
                          </div>
                        ) : (
                          <div className="bg-caramel/10 rounded-lg p-3 text-center">
                            <div className="text-xs text-chocolate">Costo de envío</div>
                            <div className="text-2xl font-bold text-caramel">${deliveryInfo.delivery_cost.toFixed(2)}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Scheduled Date/Time */}
              <div className="space-y-3 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-purple-900 flex items-center gap-2">
                  📅 ¿Cuándo lo necesitas?
                </p>
                
                {(() => {
                  const location = deliveryMethod === 'tienda' ? selectedLocation : 'matriz';
                  const hours = getBusinessHoursForDate(scheduledDate, location);
                  return hours ? (
                    <p className="text-xs text-purple-600 bg-purple-100 p-2 rounded">
                      🕐 Horario: {hours.open} - {hours.close}
                    </p>
                  ) : (
                    <p className="text-xs text-red-600 bg-red-100 p-2 rounded font-medium">
                      ⚠️ Cerrado este día
                    </p>
                  );
                })()}
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="scheduled-date" className="text-xs text-purple-700">Fecha</Label>
                    <Input
                      id="scheduled-date"
                      type="date"
                      value={scheduledDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="scheduled-time" className="text-xs text-purple-700">Hora</Label>
                    <Input
                      id="scheduled-time"
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className={`bg-white ${
                        validateScheduledTime().blocking 
                          ? 'border-red-500 border-2' 
                          : validateScheduledTime().warning 
                            ? 'border-yellow-500 border-2' 
                            : 'border-green-500 border-2'
                      }`}
                    />
                  </div>
                </div>
                {validateScheduledTime().blocking && validateScheduledTime().error && (
                  <p className="text-xs text-red-600 font-medium bg-red-50 p-2 rounded">
                    ⚠️ {validateScheduledTime().error}
                  </p>
                )}
                {validateScheduledTime().warning && (
                  <p className="text-xs text-yellow-700 font-medium bg-yellow-50 p-2 rounded border border-yellow-300">
                    {validateScheduledTime().warning}
                  </p>
                )}
                <p className="text-xs text-purple-600">
                  {scheduledDate === new Date().toISOString().split('T')[0] 
                    ? '📍 Entrega para hoy' 
                    : `📍 Programado para: ${new Date(scheduledDate + 'T00:00:00').toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long' })}`
                  } a las {scheduledTime}
                </p>
              </div>

              {/* Customer Phone */}
              <div className="space-y-2">
                <Label htmlFor="customer-phone">Teléfono de Contacto *</Label>
                <Input
                  id="customer-phone"
                  type="tel"
                  placeholder="0999999999"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="border-chocolate/20"
                />
              </div>

              {/* Comments */}
              <div className="space-y-2">
                <Label htmlFor="comments">Comentarios</Label>
                <Textarea
                  id="comments"
                  placeholder="Alguna nota o preferencia especial..."
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="border-chocolate/20"
                  rows={3}
                />
              </div>

              {/* Important Notes */}
              <div className="bg-cream border border-chocolate/20 rounded-lg p-4 space-y-2">
                <p className="text-xs font-semibold text-chocolate">⚠️ NOTAS IMPORTANTES:</p>
                <ul className="text-xs text-chocolate-light space-y-1 list-disc list-inside">
                  <li>Las porciones están sujetas a disponibilidad</li>
                  <li>Algunos sabores pueden demorar hasta 2 horas en preparación</li>
                </ul>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleWhatsAppOrder}
                disabled={isSubmitting}
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-70 text-white py-6 text-lg"
                size="lg"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                    Enviando pedido...
                  </>
                ) : (
                  <>
                    <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Enviar Pedido por WhatsApp
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
